import { PrintIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../../../../api/apis.config'
import { diffuseHostFamilies } from '../../../../../../api/centers/diffuse-host-family'
import { getCenter } from '../../../../../../api/centers/get-center'
import { getCenterAccomodation } from '../../../../../../api/centers/get-center-accomodation'
import { getCenterOl } from '../../../../../../api/centers/get-center-ol'
import { getOlPermissions } from '../../../../../../lib/constants/ol-permissions'
import useAuth from '../../../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../../../lib/hooks/use-data-props'
import { useDownloadDocuments } from '../../../../../../lib/hooks/use-download-documents'
import { useModalManager } from '../../../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../../../lib/hooks/use-translate'
import { AccomodationEnum } from '../../../../../../lib/types/enums/accomodation-type/accomodation-type'
import { RoleEnum } from '../../../../../../lib/types/enums/users/role.enum'
import { CenterBasic } from '../../../../../../lib/types/models/center/center'
import { EnrollmentFilter } from '../../../../../../lib/types/models/enrollment/enrollment-filter'
import { EnrollmentCenter } from '../../../../../../lib/types/models/enrollment-center/enrollment-center'
import { DataProps } from '../../../../../../lib/types/utils'
import DynamicDataTable, { DynamicTableRef } from '../../../../../elements/dynamic-table'
import { EnrollmentProfileModel } from '../../../../enrollment-profile/enrollment-profile-model'
import { getEnrollmentColumns } from '../../../../ol-common/ol-center-placement-enrollment-table/utils'

const CenterStudentsDataTable = (
  props: DataProps<CenterBasic, { onHostFamilyDiffused?: () => void }>
) => {
  const { t } = useTranslate()
  const { user } = useAuth()
  const tableRef = useRef<DynamicTableRef<EnrollmentCenter>>(null)
  const { downloadDocuments } = useDownloadDocuments()
  const { toggleModal, closeModel, modalState, selectedData } = useModalManager<
    'view-pdf',
    { pdfLink: string; enrollmentId: number }
  >(['view-pdf'])

  const { id: centerId, data } = useDataProps(props, (id) => getCenter(id, 'BASIC'))

  const { data: ol } = useQuery({
    queryKey: ['ol', centerId],
    queryFn: () => getCenterOl(centerId)
  })

  const { data: centerAccomodation } = useQuery({
    queryKey: ['center-accomodation', centerId],
    queryFn: () => getCenterAccomodation(centerId)
  })

  const olPermissions = getOlPermissions({ role: user.role, type: ol?.type })

  const diffuseHostFamiliesToParents = useMutation({
    mutationKey: ['diffuse-host-family-to-parents'],
    mutationFn: diffuseHostFamilies,
    onSuccess: () => {
      toast.success(t('ol.center.placement.diffusion.host.family.success'))
      props.onHostFamilyDiffused?.()
    }
  })

  async function handleProfilePdfClick(enrollmentId: number) {
    const { fileLink } = await downloadDocuments(
      {
        endpoint: constructApiUrl(API.enrollments.exportEnrollmentProfilePdf.url, {
          id: enrollmentId
        })
      },
      { automaticallyDownload: false }
    )

    if (fileLink) toggleModal('view-pdf', { pdfLink: fileLink, enrollmentId })
  }

  if (!data) return null

  return (
    <>
      <DynamicDataTable<EnrollmentCenter, EnrollmentFilter>
        ref={tableRef}
        className="bg-white-hover p-0"
        bodyVariant="white"
        columns={getEnrollmentColumns({
          t,
          accomodation: data.accomodation,
          userRoles: [RoleEnum.ADMIN],
          onNameClick: async (enrollmentCenter) =>
            await handleProfilePdfClick(enrollmentCenter.enrollmentId),
          onCenterGroupChange: () => tableRef.current?.refetch()
        })}
        headerType="actions-only"
        version="PARTIAL"
        selectable
        allowSearchParam
        endpoint={constructApiUrl(API.centers.getEnrollments.url, { id: centerId })}
        permissions={{ delete: false }}
        actions={[
          {
            type: 'multiple',
            variant: 'primary',
            label: t('ol.center.enrollment.table.diffusion.button'),
            refetch: true,
            fn: (rows) => {
              diffuseHostFamiliesToParents.mutate({
                centerId,
                enrollmentIds: rows.map((c) => c.enrollmentId)
              })
              tableRef.current?.refetch()
            }
          },
          {
            type: 'global',
            icon: PrintIcon,
            variant: 'ghost',
            hasAccess: olPermissions['print-my-placement-list-by-name-of-student'],
            label: 'Imprimer ma liste de placements par nom de jeune',
            fn: () =>
              downloadDocuments({
                endpoint: constructApiUrl(
                  API.centers.printPdfListOfPlacementsByEnrollmentName.url,
                  { id: centerId }
                )
              })
          },
          {
            type: 'global',
            icon: PrintIcon,
            variant: 'ghost',
            hasAccess:
              olPermissions['host-family'] &&
              centerAccomodation?.accomodation === AccomodationEnum.HOST_FAMILY,
            label: 'Imprimer ma liste de placements par nom de FH',
            fn: () =>
              downloadDocuments({
                endpoint: constructApiUrl(
                  API.centers.printPdfListOfEnrollmentsPlacementByHostFamily.url,
                  { id: centerId }
                )
              })
          }
        ]}
      />

      {selectedData && (
        <EnrollmentProfileModel
          id={selectedData.enrollmentId}
          src={selectedData.pdfLink}
          onEnrollmentUpdate={() => tableRef.current?.refetch()}
          isOpen={modalState['view-pdf']}
          onCancel={() => closeModel('view-pdf')}
        />
      )}
    </>
  )
}

export default CenterStudentsDataTable
